/**
 * Content Writer Workspace (Task 219 · backed by the live Task 220 backend) —
 * the role HOME for the agency `content_writer`. Unlike Designer/Video (whose
 * deliverable is a file upload), the writer authors IN-SYSTEM TEXT for each
 * assigned content variant, saved + submitted via the Task 220 draft EFs.
 *
 * Assigned-only: queue from `list-my-production-tasks` (RLS assignee-match),
 * filtered to WRITING tasks (Task 292C1: `assignment_metadata.discipline`, not
 * the item's discipline, which stays `content`), merged with the caller's own
 * `list-content-writer-drafts`. No client-scope provider, no org-wide reader.
 *
 * Reachable two ways: rendered inline by `AppLanding` at `/app` (the writer's
 * role home) and by the canonical deep link `/app/me/writing/:taskId` added in
 * Task 292C1-QA-A, which selects one task by its STABLE task id.
 *
 * Backend (Task 220 / 213, live):
 *   - save-content-writer-draft / submit-content-writer-draft (the text flow)
 *
 * Task 292C1-QA-A REMOVED start / request-clarification / block from this
 * surface: all three are PRODUCTION RPCs gated on
 * `parent content_item must be in_production`, which a writing task's item can
 * never be, so every one of them returned HTTP 400. The writing lane's work
 * state is `content_writer_drafts.status`, not `tasks.status`.
 *
 * The senior REVIEW actions live in the SeniorContentWriterWorkspace, never here.
 *
 * RTL. Reuses the `.dwk-*` production-workspace layout.
 */

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type {
  ContentWriterDraftRow,
  ListContentVariantsData,
  ListContentWriterDraftsData,
  ListMyProductionTasksData,
  MeContext,
  MyProductionTask,
} from '../api/types';
import { useNavigate, useParams } from 'react-router-dom';

import { LoadingState, PermissionDeniedState } from '../components/states';
import { useMeContext } from '../context/MeContextProvider';

import {
  listContentVariants,
  listContentWriterDrafts,
  listMyProductionTasks,
  saveContentWriterDraft,
  submitContentWriterDraft,
} from '../api/endpoints';
import { isApiError } from '../api/client';
import { avatarColor, initial, SOON } from './ownerCommandCenter.logic';
import {
  buildPlatformSpecificFromEdits,
  editorIdentityKey,
  isEditorDirty,
  shouldHydrateEditor,
  SUBMIT_CONFLICT_AR,
  UNSAVED_BADGE_AR,
  UNSAVED_LEAVE_CONFIRM_AR,
  type WriterEditorBuffers,
} from './contentWriterWorkspace.logic';
import {
  canOpenWriterAiGeneration,
  canOpenWriterWorkspace,
  resolveWritingTaskById,
  shouldConfirmTaskSwitch,
  writingTaskHref,
  writingTasksOnly,
  disciplineLabelAr,
  draftStatusLabelAr,
  draftsByContentItem,
  groupContentTasksByLane,
  platformLabelAr,
  psFieldToString,
  resolveSelected,
  WRITER_TEXT_FIELDS,
  writerPsFields,
} from './contentWriterWorkspace.logic';
import type { ContentVariantRow } from './client/productionBrief';
import {
  EMPTY_BRIEF_AR,
  hasAnyDesignerBrief,
  isVideoBriefPlatform,
  presentStaticBriefFields,
  presentVideoBriefFields,
} from './client/productionBrief';
import { filterTasksByClient } from './productionWorkspace.logic';
import { useProductionClientSelection } from './useProductionClientSelection';
import { ContentWriterAiPanel } from './ContentWriterAiPanel';

interface Props {
  readonly me: MeContext;
}

type TasksState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly tasks: ReadonlyArray<MyProductionTask> }
  | { readonly status: 'error' };
type DraftsState = ReadonlyArray<ContentWriterDraftRow>;
type VariantState =
  | { readonly status: 'idle' | 'no_item' | 'loading' | 'error' }
  | {
      readonly status: 'ready';
      readonly contentItemId: string;
      readonly variants: ReadonlyArray<ContentVariantRow>;
    };

/**
 * Task 292B3 — a 409 from the atomic submit means the canonical variant moved
 * on since this draft was written (the Task 292B2 base-revision guard). It is
 * NOT a validation failure: the writer's text is fine, it just needs
 * reconciling, so the buffers are left exactly as they are.
 */
function isConflictError(err: unknown): boolean {
  return isApiError(err) && (err.status === 409 || err.code === 'conflict');
}

function actionErrorMessage(err: unknown): string {
  if (err instanceof Error && err.message.trim().length > 0) return err.message;
  return 'تعذّر تنفيذ الإجراء. حاول مرّة أخرى.';
}

/**
 * Task 292C1-QA-A — routed entry point for the canonical writer deep link
 * `/app/me/writing` and `/app/me/writing/:taskId`.
 *
 * Before this, ContentWriterWorkspace was only ever rendered INLINE by
 * AppLanding at `/app`, so no URL could address it and therefore no URL could
 * address a task inside it. «افتح المهمة» had nowhere correct to point.
 *
 * AppLanding's inline render is unchanged; this wrapper supplies `me` from
 * context AND enforces the route's authorization.
 *
 * AUTHORIZATION (Codex round 1, P1 — do not weaken): the route requires
 * `canOpenWriterWorkspace` — an active agency `content_writer` holding
 * `task.writing.execute`, sysadmins excluded. An earlier revision had no gate,
 * arguing that `/app/me/tasks` has none either; that was wrong. `me/tasks` is a
 * GENERIC queue, whereas this is a ROLE-SPECIFIC workspace that AppLanding only
 * ever routes the authoring content_writer to, so an ungated URL bypassed that
 * precedence — contrary to SECURITY.md line 29.
 *
 * RLS remains the backstop, not the gate: the only data source is
 * `list-my-production-tasks`, bound to `assignee_user_id = auth.uid()` and then
 * filtered to writing tasks. "The backend would refuse the mutation" is not a
 * substitute for an authorization check on the route.
 */
export function ContentWriterWorkspaceRoute(): JSX.Element {
  const { me } = useMeContext();
  if (!me) return <LoadingState title="جاري التحميل" />;
  // Codex round 1 (P1) — an explicit route gate. Without it any authenticated
  // user could open the writer surface by URL, bypassing AppLanding's role
  // precedence and SECURITY.md line 29. The queue is RLS-bound so no data would
  // have leaked, but "the backend would refuse the mutation" is not the same as
  // an authorization check on the route.
  if (!canOpenWriterWorkspace(me)) return <PermissionDeniedState />;
  return <ContentWriterWorkspace me={me} />;
}

export function ContentWriterWorkspace({ me }: Props) {
  const writerName = me.profile?.full_name ?? me.profile?.email ?? 'كاتب المحتوى';
  const { selectedClientId } = useProductionClientSelection(me.client_assignments);

  // Task 292C1-QA-A — the URL is the source of truth for WHICH task is open.
  // `/app/me/writing/:taskId` is the canonical deep link; `/app` (the writer's
  // landing, rendered inline by AppLanding) has no param and keeps the previous
  // "first task in the queue" behaviour.
  const { taskId: routeTaskId } = useParams<{ taskId?: string }>();
  const navigate = useNavigate();

  const [tasksState, setTasksState] = useState<TasksState>({ status: 'loading' });
  const [drafts, setDrafts] = useState<DraftsState>([]);
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(routeTaskId ?? null);

  const [variantState, setVariantState] = useState<VariantState>({ status: 'idle' });
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);

  // Editable draft form for the selected variant.
  const [fHeadline, setFHeadline] = useState('');
  const [fCaption, setFCaption] = useState('');
  const [fBody, setFBody] = useState('');
  const [fPs, setFPs] = useState<Record<string, string>>({});

  // Task 292B3 — the baseline the buffers were hydrated from, plus the identity
  // it belongs to. `dirty` is derived from it; there is no separate flag to fall
  // out of sync.
  const [baseline, setBaseline] = useState<{
    readonly key: string;
    readonly buffers: WriterEditorBuffers;
    /**
     * The canonical `content_variants.updated_at` these buffers were hydrated
     * from. Submit declares THIS, never the live query value: while the editor
     * is dirty a background refetch keeps advancing the live value, so
     * declaring that would tell the server "I edited the newest revision" when
     * the writer never saw it — silently defeating the 292B2 guard.
     */
    readonly canonicalUpdatedAt: string | null;
    /**
     * The EXACT raw `platform_specific` these buffers correspond to — the
     * object last synced with the server, not a cached query row. Rebuilding
     * from `draftForVariant.platform_specific` instead would use PRE-SAVE data
     * until the refetch lands, so "edit A -> Save -> edit B -> Submit" would
     * restore A's old raw value while applying B.
     */
    readonly rawPs: Readonly<Record<string, unknown>>;
  } | null>(null);

  /**
   * Set when the server rejected a submit with 409. The next press deliberately
   * declares the FRESHLY REFETCHED canonical revision (a reload is triggered on
   * conflict), rather than a value frozen at error time which could already be
   * stale and would 409 forever.
   */
  const [conflictOverride, setConflictOverride] = useState(false);

  const [inFlight, setInFlight] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  // ── Assigned-only feed + the caller's own drafts. ──
  useEffect(() => {
    const ctrl = new AbortController();
    setTasksState((prev) => (prev.status === 'ready' ? prev : { status: 'loading' }));
    Promise.all([
      listMyProductionTasks({ signal: ctrl.signal }),
      listContentWriterDrafts({ signal: ctrl.signal }),
    ])
      .then(([tasksData, draftsData]: [ListMyProductionTasksData, ListContentWriterDraftsData]) => {
        if (ctrl.signal.aborted) return;
        setTasksState({ status: 'ready', tasks: writingTasksOnly(tasksData.tasks) });
        setDrafts(draftsData.drafts);
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted || (err instanceof DOMException && err.name === 'AbortError'))
          return;
        setTasksState({ status: 'error' });
      });
    return () => ctrl.abort();
  }, [reloadKey]);

  const unfilteredTasks = tasksState.status === 'ready' ? tasksState.tasks : [];
  const allTasks = filterTasksByClient(unfilteredTasks, selectedClientId);
  const scopedDrafts = selectedClientId
    ? drafts.filter((draft) => draft.client_id === selectedClientId)
    : drafts;
  const lanes = groupContentTasksByLane(allTasks, scopedDrafts);
  const selectedTask = resolveWritingTaskById(allTasks, selectedTaskId);
  const selectedItemId = selectedTask?.content_item?.id ?? null;
  const draftMap = draftsByContentItem(scopedDrafts);

  const briefState: VariantState =
    variantState.status === 'ready' && variantState.contentItemId !== selectedItemId
      ? { status: 'loading' }
      : variantState;

  // ── Selected task's content variants (brief + canonical/AI source text). ──
  useEffect(() => {
    if (!selectedTask) {
      setVariantState({ status: 'idle' });
      return;
    }
    if (!selectedItemId) {
      setVariantState({ status: 'no_item' });
      return;
    }
    const ctrl = new AbortController();
    setVariantState({ status: 'loading' });
    listContentVariants(selectedItemId, { signal: ctrl.signal })
      .then((data: ListContentVariantsData) => {
        if (ctrl.signal.aborted) return;
        setVariantState({
          status: 'ready',
          contentItemId: selectedItemId,
          variants: data.content_variants,
        });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted || (err instanceof DOMException && err.name === 'AbortError'))
          return;
        setVariantState({ status: 'error' });
      });
    return () => ctrl.abort();
  }, [selectedTask?.assignment_id, selectedItemId, reloadKey]);

  const variants = briefState.status === 'ready' ? briefState.variants : [];
  const selectedVariant = resolveSelected(variants, selectedVariantId);
  const selectedVariantId2 = selectedVariant?.id ?? null;
  const draftForVariant = selectedVariantId2
    ? (scopedDrafts.find((d) => d.content_variant_id === selectedVariantId2) ?? null)
    : null;
  const draftLocked =
    draftForVariant !== null &&
    (draftForVariant.status === 'submitted_to_senior' || draftForVariant.status === 'approved');

  // Prefill the form whenever the selected variant changes: from the writer's
  // own draft if present, else from the canonical/AI source variant text.
  useEffect(() => {
    setActionError(null);
    setActionSuccess(null);
    const src: {
      headline: string | null;
      caption: string | null;
      body: string | null;
      ps: Readonly<Record<string, unknown>>;
    } | null = draftForVariant
      ? {
          headline: draftForVariant.headline,
          caption: draftForVariant.caption,
          body: draftForVariant.body,
          ps: draftForVariant.platform_specific,
        }
      : selectedVariant
        ? {
            headline: selectedVariant.headline ?? null,
            caption: selectedVariant.caption ?? null,
            body: selectedVariant.body ?? null,
            ps: selectedVariant.platform_specific ?? {},
          }
        : null;
    const psObj: Record<string, string> = {};
    if (src) {
      for (const f of writerPsFields(selectedVariant?.platform ?? null)) {
        psObj[f.key] = psFieldToString(src.ps, f.key);
      }
    }
    const next: WriterEditorBuffers = {
      headline: src?.headline ?? '',
      caption: src?.caption ?? '',
      body: src?.body ?? '',
      ps: psObj,
    };

    // Task 292B3 — NEVER clobber unsaved work. Before this, the effect wrote
    // the server value into all four buffers on every refetch, so a background
    // reload silently destroyed whatever the writer had typed.
    const key = editorIdentityKey(selectedVariantId2);
    const current: WriterEditorBuffers = {
      headline: fHeadline,
      caption: fCaption,
      body: fBody,
      ps: fPs,
    };
    const hydrate = shouldHydrateEditor({
      hasBaseline: baseline !== null,
      identityChanged: baseline?.key !== key,
      dirty: isEditorDirty(current, baseline?.buffers ?? null),
      // "the server data actually moved away from what we hydrated"
      serverRevisionChanged: isEditorDirty(next, baseline?.buffers ?? null),
      // A canonical edit can touch only fields the writer never sees, leaving
      // the buffers identical. Accepting it here keeps the declared base fresh,
      // which is what stops a spurious 409 on an honest submit.
      canonicalRevisionChanged:
        (selectedVariant?.updated_at ?? null) !== (baseline?.canonicalUpdatedAt ?? null),
    });
    if (!hydrate) return;

    setFHeadline(next.headline);
    setFCaption(next.caption);
    setFBody(next.body);
    setFPs(next.ps);
    setBaseline({
      key,
      buffers: next,
      canonicalUpdatedAt: selectedVariant?.updated_at ?? null,
      rawPs: src?.ps ?? {},
    });
    // Re-evaluated when the source text changes too (a refetch can update the
    // draft or canonical variant for the same id/status), but the guard above
    // decides whether that may touch the buffers.
  }, [
    selectedVariantId2,
    draftForVariant?.id,
    draftForVariant?.status,
    draftForVariant?.updated_at,
    selectedVariant?.headline,
    selectedVariant?.caption,
    selectedVariant?.body,
    selectedVariant?.platform,
    // Task 292B3 — a canonical revision that changes ONLY platform_specific
    // must still be evaluated by the guard above (which decides whether it may
    // touch the buffers).
    selectedVariant?.platform_specific,
    selectedVariant?.updated_at,
  ]);

  // Task 292B3 — derived dirty state (no separate flag to desync) and the
  // guards that stop it being lost.
  const currentBuffers: WriterEditorBuffers = {
    headline: fHeadline,
    caption: fCaption,
    body: fBody,
    ps: fPs,
  };
  const dirty = isEditorDirty(currentBuffers, baseline?.buffers ?? null);

  // Back / forward / direct entry all surface as a changed route param, so the
  // URL — not a click handler — is what re-selects the task.
  //
  // Codex round 1 (P1) — this must honour the SAME unsaved-work guard as a
  // click. `selectTask` confirms, but browser back/forward never goes through
  // it, and `beforeunload` does not fire for SPA history navigation, so a Back
  // press after editing used to discard the writer's text silently.
  //
  // On refusal the URL is put back with `replace`, which re-runs this effect
  // with a param that already equals the current selection — so the guard is
  // not re-asked and there is no loop.
  useEffect(() => {
    const next = routeTaskId ?? null;
    if (next === selectedTaskId) return;
    if (
      shouldConfirmTaskSwitch({ dirty, fromTaskId: selectedTaskId, toTaskId: next }) &&
      !window.confirm(UNSAVED_LEAVE_CONFIRM_AR)
    ) {
      if (selectedTaskId) navigate(writingTaskHref(selectedTaskId), { replace: true });
      return;
    }
    setSelectedTaskId(next);
    setSelectedVariantId(null);
    // Intentionally keyed on the ROUTE only. `dirty` / `selectedTaskId` are
    // read, not tracked: re-running when the editor becomes dirty would
    // re-evaluate a transition that already completed.
  }, [routeTaskId]);

  // Closing / reloading the tab while dirty. The browser shows its own native
  // prompt for this; we never call `alert()` ourselves.
  useEffect(() => {
    if (!dirty) return;
    function onBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  /** Confirm before an in-app switch that would discard unsaved work. */
  function confirmDiscardIfDirty(): boolean {
    if (!dirty) return true;
    return window.confirm(UNSAVED_LEAVE_CONFIRM_AR);
  }

  function reload() {
    setReloadKey((n) => n + 1);
  }
  function selectTask(id: string) {
    if (!confirmDiscardIfDirty()) return;
    setSelectedTaskId(id);
    setSelectedVariantId(null);
    // Push the canonical URL so the selection survives refresh and is
    // reachable by back/forward. The effect above reconciles state from the
    // param, so this stays the single source of truth.
    navigate(writingTaskHref(id));
  }

  async function handleSave() {
    if (inFlight || !selectedVariant) return;
    setInFlight(true);
    setActionError(null);
    setActionSuccess(null);
    // Built from the baseline's RAW payload (what was last synced), so an edit
    // saved a moment ago is not reverted by a stale cached draft row.
    const nextRawPs = buildPlatformSpecificFromEdits(
      baseline?.rawPs ?? draftForVariant?.platform_specific ?? selectedVariant.platform_specific,
      baseline?.buffers.ps ?? {},
      fPs,
    );
    try {
      await saveContentWriterDraft({
        input: {
          content_variant_id: selectedVariant.id,
          headline: fHeadline,
          caption: fCaption,
          body: fBody,
          platform_specific: nextRawPs,
        },
      });
      // Task 292B3 — a successful save makes the CURRENT buffers the baseline,
      // which clears dirty without touching what the writer sees. A failure
      // falls through to the catch and leaves every typed value in place.
      setBaseline({
        key: editorIdentityKey(selectedVariantId2),
        buffers: { headline: fHeadline, caption: fCaption, body: fBody, ps: fPs },
        canonicalUpdatedAt: baseline?.canonicalUpdatedAt ?? selectedVariant.updated_at,
        // Keep the RAW payload in step with what was just sent.
        rawPs: nextRawPs,
      });
      setActionSuccess('تم حفظ المسودّة.');
      reload();
    } catch (err) {
      setActionError(actionErrorMessage(err));
    } finally {
      setInFlight(false);
    }
  }

  async function handleSubmit() {
    if (inFlight || !selectedVariant) return;
    setInFlight(true);
    setActionError(null);
    setActionSuccess(null);
    const nextRawPs = buildPlatformSpecificFromEdits(
      baseline?.rawPs ?? draftForVariant?.platform_specific ?? selectedVariant.platform_specific,
      baseline?.buffers.ps ?? {},
      fPs,
    );
    // After a conflict the writer is deliberately overriding, so declare the
    // FRESHLY REFETCHED canonical revision rather than the baseline's.
    const declaredBase = conflictOverride
      ? selectedVariant.updated_at
      : (baseline?.canonicalUpdatedAt ?? selectedVariant.updated_at);
    try {
      // Task 292B3 — send EXACTLY what is on screen. The server saves this
      // payload and submits the same draft in one transaction, so the old
      // "save once -> keep typing -> send the older saved text" loss is gone,
      // and submitting works even if Save was never pressed.
      await submitContentWriterDraft({
        input: {
          content_variant_id: selectedVariant.id,
          headline: fHeadline,
          caption: fCaption,
          body: fBody,
          platform_specific: nextRawPs,
          // The revision the BASELINE was hydrated from — never the live query
          // value, which a background refetch keeps advancing. (After a
          // conflict the writer overrides deliberately; see `declaredBase`.)
          base_variant_updated_at: declaredBase,
        },
      });
      // Success is only announced AFTER the atomic server result.
      setBaseline({
        key: editorIdentityKey(selectedVariantId2),
        buffers: { headline: fHeadline, caption: fCaption, body: fBody, ps: fPs },
        canonicalUpdatedAt: declaredBase,
        rawPs: nextRawPs,
      });
      setConflictOverride(false);
      setActionSuccess('أُرسلت المسودّة لمراجعة كبير الكتّاب.');
      reload();
    } catch (err) {
      // A 409 means the canonical variant moved on. The buffers are deliberately
      // untouched so nothing typed is lost.
      //
      // The editor is dirty at this point, so the hydration guard will not pull
      // the new canonical revision in — without the line below the writer would
      // be permanently stuck, warned on every attempt and unable to send at all.
      // So the conflict is a ONE-TIME, EXPLICIT warning: the declared base moves
      // to the current canonical revision, and a second deliberate press sends
      // the writer's text over it. That is never SILENT, which is the property
      // Task 292B2 protects.
      if (isConflictError(err)) {
        // Do NOT freeze a timestamp here: the value in the client may already be
        // older than the revision that caused the conflict, which would 409
        // forever. Refetch, and let the next deliberate press declare whatever
        // the server actually has by then.
        setConflictOverride(true);
        reload();
        setActionError(SUBMIT_CONFLICT_AR);
      } else {
        setActionError(actionErrorMessage(err));
      }
    } finally {
      setInFlight(false);
    }
  }

  return (
    <div className="dwk cww" dir="rtl">
      <div className="dwk-top">
        <div className="dwk-greet">
          <h1 className="h-page">
            مساحة{' '}
            <em className="serif italic" style={{ color: 'var(--tangerine)' }}>
              كتابة المحتوى
            </em>
          </h1>
          <p className="dwk-sub">
            أهلاً {writerName} — مهامك المسندة وكتابة النصوص ومراجعة كبير الكتّاب في مكان واحد.
          </p>
        </div>
      </div>

      {/* Task 292C3 — writer AI generation entry point. Renders above
          the studio so it is discoverable without hiding the queue.
          Codex round 7 (P2) — even though this workspace is
          route-gated to writers, wrap the panel in its own
          `canOpenWriterAiGeneration` check as defence-in-depth: if a
          future refactor lands ContentWriterWorkspace behind a broader
          route (e.g. a dual-hat producer surface), a non-writer would
          otherwise see a form that the server will always refuse.
          Every scope check is still server-side; this gate is
          presentation only. */}
      {canOpenWriterAiGeneration(me) && (
        <ContentWriterAiPanel
          me={me}
          onSaved={(firstTaskId) => {
            reload();
            navigate(writingTaskHref(firstTaskId));
          }}
        />
      )}

      <div className="dwk-studio">
        {/* QUEUE */}
        <section className="dwk-pane">
          <div className="dwk-pane-head">
            <span className="dwk-ph-t">
              طابور <em>الكتابة</em>
            </span>
            <span className="dwk-ph-c">
              {tasksState.status === 'ready' ? allTasks.length : SOON}
            </span>
          </div>
          <div className="dwk-queue">
            {tasksState.status === 'loading' && (
              <div className="occ-empty sm">جارٍ تحميل مهامك…</div>
            )}
            {tasksState.status === 'error' && (
              <div className="occ-empty sm">
                تعذّر تحميل المهام.{' '}
                <button type="button" className="dwk-retry" onClick={reload}>
                  إعادة المحاولة
                </button>
              </div>
            )}
            {tasksState.status === 'ready' && allTasks.length === 0 && (
              <div className="dwk-queue-note occ-empty sm">
                لا توجد مهام كتابة مُسنَدة إليك حالياً.
              </div>
            )}
            {tasksState.status === 'ready' &&
              allTasks.length > 0 &&
              lanes.map((lane) =>
                lane.tasks.length > 0 ? (
                  <div
                    key={lane.key}
                    className={`dwk-lane ${lane.key}${lane.deemphasized ? ' muted' : ''}`}
                  >
                    <div className="dwk-lane-head">
                      {lane.label} <span className="dwk-gc">{lane.tasks.length}</span>
                    </div>
                    {lane.tasks.map((t) => (
                      <TaskCard
                        key={t.assignment_id}
                        task={t}
                        draftCount={
                          (t.content_item?.id ? draftMap.get(t.content_item.id) : undefined)
                            ?.length ?? 0
                        }
                        selected={selectedTask?.task_id === t.task_id}
                        deemphasized={lane.deemphasized}
                        onSelect={() => selectTask(t.task_id)}
                      />
                    ))}
                  </div>
                ) : null,
              )}
          </div>
        </section>

        {/* DETAIL — editable draft */}
        <section className="dwk-pane">
          <div className="dwk-pane-head">
            <span className="dwk-ph-t">
              كتابة <em>المسودّة</em>
            </span>
          </div>
          {!selectedTask ? (
            /*
              Task 292C1-QA-A — a deep link that does not resolve gets an
              explicit answer, not somebody else's task. The queue is RLS-scoped
              to the caller's own assignments and filtered to writing tasks, so
              "not in this list" means not yours, not a writing task, or no
              longer assigned. Deliberately NOT distinguishing those: telling an
              unauthorized caller which one it was is an existence oracle.
            */
            <div className="occ-empty">
              {routeTaskId
                ? 'هذه المهمة غير متاحة لك أو لم تعد مُسنَدة إليك. اختر مهمة من الطابور.'
                : 'اختر مهمة من الطابور لبدء الكتابة.'}
            </div>
          ) : (
            <div className="dwk-detail">
              <div className="dwk-dt-hero">
                <div className="dwk-crumb">
                  <span
                    className="dwk-dc-av"
                    style={{
                      background: avatarColor(
                        selectedTask.client?.id ?? selectedTask.assignment_id,
                      ),
                    }}
                  >
                    {initial(selectedTask.client?.name ?? '؟')}
                  </span>
                  <span className="dwk-dc-nm">{selectedTask.client?.name ?? 'عميل غير متاح'}</span>
                </div>
                <div className="dwk-dt-title">
                  {selectedTask.content_item?.title ?? 'مهمة بدون عنوان'}
                </div>
              </div>

              {/* variant (platform) selector */}
              {variants.length > 1 && (
                <div className="dwk-vchips" role="group" aria-label="نسخ المنصّات">
                  {variants.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      className={`dwk-vchip${v.id === selectedVariant?.id ? ' active' : ''}`}
                      aria-pressed={v.id === selectedVariant?.id}
                      onClick={() => {
                        if (!confirmDiscardIfDirty()) return;
                        setSelectedVariantId(v.id);
                      }}
                    >
                      {platformLabelAr(v.platform)}
                    </button>
                  ))}
                </div>
              )}

              {draftForVariant && (
                <div className="cww-draft-state">
                  حالة المسودّة: <strong>{draftStatusLabelAr(draftForVariant.status)}</strong>
                </div>
              )}
              {draftForVariant?.status === 'returned_with_notes' &&
                draftForVariant.senior_notes && (
                  <div className="dwk-block-reason">
                    <span className="dwk-block-reason-t">ملاحظات كبير الكتّاب</span>
                    <span className="dwk-block-reason-v">{draftForVariant.senior_notes}</span>
                  </div>
                )}

              {briefState.status === 'loading' && <div className="occ-empty sm">جارٍ التحميل…</div>}
              {briefState.status === 'error' && (
                <div className="occ-empty sm">
                  تعذّر تحميل النسخة.{' '}
                  <button type="button" className="dwk-retry" onClick={reload}>
                    إعادة المحاولة
                  </button>
                </div>
              )}
              {briefState.status === 'ready' && !selectedVariant && (
                <div className="occ-empty sm">لا توجد نسخة محتوى مرتبطة بهذه المهمة بعد.</div>
              )}

              {selectedVariant && (
                <div className="cww-form">
                  {draftLocked && (
                    <div className="cww-locked-note">
                      المسودّة {draftStatusLabelAr(draftForVariant!.status)} — للقراءة فقط حتى
                      يراجعها كبير الكتّاب.
                    </div>
                  )}
                  {WRITER_TEXT_FIELDS.map((f) => (
                    <Field
                      key={f.key}
                      field={f}
                      value={
                        f.key === 'headline' ? fHeadline : f.key === 'caption' ? fCaption : fBody
                      }
                      disabled={draftLocked || inFlight}
                      onChange={(v) =>
                        f.key === 'headline'
                          ? setFHeadline(v)
                          : f.key === 'caption'
                            ? setFCaption(v)
                            : setFBody(v)
                      }
                    />
                  ))}
                  {writerPsFields(selectedVariant.platform).map((f) => (
                    <Field
                      key={f.key}
                      field={f}
                      value={fPs[f.key] ?? ''}
                      disabled={draftLocked || inFlight}
                      onChange={(v) => setFPs((prev) => ({ ...prev, [f.key]: v }))}
                    />
                  ))}

                  <div className="dwk-actions">
                    {/*
                      Task 292C1-QA-A — «بدء المهمة», «طلب توضيح» and «تعليم محظور»
                      were removed from this surface. All three call PRODUCTION
                      RPCs (start / block / request_clarification), and every one
                      of them requires `parent content_item must be in_production`.
                      A writing task's item is `draft` or
                      `pending_client_content_approval` BY CONSTRUCTION — the
                      handoff refuses anything from `client_content_approved`
                      onward — so the gate could never be satisfied and all three
                      returned 23514 -> validation_error -> HTTP 400 every time.

                      The writing lane already carries its own work state in
                      `content_writer_drafts.status`, so saving a draft IS the
                      "started" signal (the queue shows the draft count, not the
                      task status). Nothing was weakened: save, submit, the
                      conflict guard and the senior-review cycle are untouched.
                    */}
                    <button
                      type="button"
                      className="dwk-act dark"
                      disabled={draftLocked || inFlight}
                      onClick={handleSave}
                    >
                      {inFlight ? 'جارٍ…' : 'حفظ المسودّة'}
                    </button>
                    <button
                      type="button"
                      className="dwk-act primary"
                      disabled={draftLocked || inFlight}
                      onClick={handleSubmit}
                    >
                      {inFlight ? 'جارٍ…' : 'إرسال لكبير الكتّاب'}
                    </button>
                    {/* Task 292B3 — restrained unsaved-changes affordance, next
                        to the actions it affects. Submit sends these buffers
                        atomically, so this is a status hint, not a warning. */}
                    {dirty && !draftLocked ? (
                      <span className="pill pill-review cww-unsaved">{UNSAVED_BADGE_AR}</span>
                    ) : null}
                  </div>
                  {actionError !== null && <div className="dwk-act-msg err">{actionError}</div>}
                  {actionSuccess !== null && <div className="dwk-act-msg ok">{actionSuccess}</div>}
                </div>
              )}
            </div>
          )}
        </section>

        {/* BRIEF — read-only context */}
        <section className="dwk-pane">
          <div className="dwk-pane-head">
            <span className="dwk-ph-t">
              موجز <em>الإنتاج</em>
            </span>
            <span className="dwk-ph-c">للقراءة فقط</span>
          </div>
          {selectedVariant ? (
            <BriefPanel variant={selectedVariant} />
          ) : (
            <div className="occ-empty">يظهر الموجز عند اختيار نسخة.</div>
          )}
        </section>
      </div>
    </div>
  );
}

function Field({
  field,
  value,
  disabled,
  onChange,
}: {
  readonly field: { readonly key: string; readonly label: string; readonly multiline: boolean };
  readonly value: string;
  readonly disabled: boolean;
  readonly onChange: (v: string) => void;
}) {
  return (
    <label className="cww-field">
      <span className="cww-field-l">{field.label}</span>
      {field.multiline ? (
        <textarea
          className="cww-field-t"
          value={value}
          disabled={disabled}
          rows={3}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          type="text"
          className="cww-field-i"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </label>
  );
}

function TaskCard({
  task,
  draftCount,
  selected,
  deemphasized,
  onSelect,
}: {
  readonly task: MyProductionTask;
  readonly draftCount: number;
  readonly selected: boolean;
  readonly deemphasized: boolean;
  readonly onSelect: () => void;
}) {
  const clientName = task.client?.name ?? 'عميل غير متاح';
  return (
    <button
      type="button"
      className={`dwk-task${selected ? ' sel' : ''}${deemphasized ? ' muted' : ''}`}
      onClick={onSelect}
      aria-pressed={selected}
    >
      <span
        className="dwk-task-av"
        style={{ background: avatarColor(task.client?.id ?? task.assignment_id) }}
      >
        {initial(clientName)}
      </span>
      <span className="dwk-task-main">
        <span className="dwk-task-client">{clientName}</span>
        <span className="dwk-task-title">{task.content_item?.title ?? 'مهمة بدون عنوان'}</span>
        <span className="dwk-task-meta">
          {task.content_item?.discipline ? (
            <span className="dwk-chip">{disciplineLabelAr(task.content_item.discipline)}</span>
          ) : null}
          <span className="dwk-chip soft">
            {draftCount > 0 ? `${draftCount} مسودّة` : 'لم تبدأ'}
          </span>
        </span>
      </span>
    </button>
  );
}

function BriefPanel({ variant }: { readonly variant: ContentVariantRow }): ReactNode {
  if (!hasAnyDesignerBrief(variant)) return <div className="occ-empty sm">{EMPTY_BRIEF_AR}</div>;
  const staticFields = presentStaticBriefFields(variant.platform_specific);
  const videoFields = isVideoBriefPlatform(variant.platform)
    ? presentVideoBriefFields(variant.platform_specific)
    : [];
  return (
    <div className="dwk-brief">
      {staticFields.map((f) => (
        <div key={f.label} className="dwk-dsec">
          <div className="dwk-dsec-t">{f.label}</div>
          <div className="dwk-dsec-val">{f.value}</div>
        </div>
      ))}
      {videoFields.length > 0 && (
        <div className="dwk-brief-video">
          <div className="dwk-brief-video-t">توجيه الفيديو</div>
          {videoFields.map((f) => (
            <div key={f.label} className="dwk-dsec">
              <div className="dwk-dsec-t">{f.label}</div>
              <div className="dwk-dsec-val">{f.value}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
